import { useState, useEffect } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { pendo } from '@frontend/tracking';
import { SIGN_UP_URL } from '../constants';

const BANNER_STORAGE_KEY = 'schedule-banner-operatory';
const CALLOUT_STORAGE_KEY = 'schedule-callout-operatory';

type OperatoryAvailabilityVariant = 'banner' | 'callout';
const PREFIX = 'schedule-calendar';

const VARIANT_CONFIG = {
  banner: {
    storageKey: BANNER_STORAGE_KEY,
    dismissEventName: `${PREFIX}:operatory-view:availability-banner:dismiss-button`,
    signUpEventName: `${PREFIX}:operatory-view:availability-banner:sign-up-button`,
    viewAvailabilitySettingsEventName: `${PREFIX}:operatory-view:availability-banner:view-availability-settings-button`,
  },
  callout: {
    storageKey: CALLOUT_STORAGE_KEY,
    dismissEventName: `${PREFIX}:operatory-view:availability-callout:dismiss-button`,
    signUpEventName: `${PREFIX}:operatory-view:availability-callout:sign-up-button`,
    viewAvailabilitySettingsEventName: `${PREFIX}:operatory-view:availability-callout:view-availability-settings-button`,
  },
};

const getStorageKey = (baseKey: string, userId: string, locationIds: string[]) => {
  return `${baseKey}-${userId}-${[...locationIds].sort().join('-')}`;
};

export const useOperatoryAvailability = (variant: OperatoryAvailabilityVariant) => {
  const { navigate } = useSettingsNavigate();
  const [isDismissed, setIsDismissed] = useState(true); // default to dismissed until we check localStorage
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0] || 'unknown-location';
  const userId = getUser()?.userID || 'unknown-user';

  const config = VARIANT_CONFIG[variant];

  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();
  const isOperatoryAvailabilityEnabled = isV3OperatorySupported && isScheduleDefinitionsEnabled;

  useEffect(() => {
    const storageKey = getStorageKey(config.storageKey, userId, selectedLocationIds);
    const stored = localStorage.getItem(storageKey);
    setIsDismissed(stored === 'true');
  }, [config.storageKey, userId, selectedLocationIds]);

  const dismiss = () => {
    const storageKey = getStorageKey(config.storageKey, userId, selectedLocationIds);
    localStorage.setItem(storageKey, 'true');
    setIsDismissed(true);

    pendo.track(config.dismissEventName, {
      locationId,
      userId,
    });
  };

  const signUp = () => {
    window.open(SIGN_UP_URL, '_blank', 'noopener,noreferrer');

    pendo.track(config.signUpEventName, {
      locationId,
      userId,
    });
  };

  const viewAvailabilitySettings = () => {
    navigate({ to: `/schedule/availability/:locationId`, params: { locationId } });

    pendo.track(config.viewAvailabilitySettingsEventName, {
      locationId,
      userId,
    });
  };

  const shouldShow = !isDismissed;

  return {
    isDismissed,
    dismiss,
    signUp,
    shouldShow,
    isOperatoryAvailabilityEnabled,
    viewAvailabilitySettings,
  };
};

export const useOperatoryAvailabilityBanner = () => {
  const banner = useOperatoryAvailability('banner');
  return {
    isBannerDismissed: banner.isDismissed,
    dismissBanner: banner.dismiss,
    signUpBanner: banner.signUp,
    shouldShowBanner: banner.shouldShow,
    isOperatoryAvailabilityEnabled: banner.isOperatoryAvailabilityEnabled,
    viewAvailabilitySettings: banner.viewAvailabilitySettings,
  };
};

export const useOperatoryAvailabilityCallout = () => {
  const callout = useOperatoryAvailability('callout');
  return {
    isCalloutDismissed: callout.isDismissed,
    dismissCallout: callout.dismiss,
    signUpCallout: callout.signUp,
    shouldShowCallout: callout.shouldShow,
    isOperatoryAvailabilityEnabled: callout.isOperatoryAvailabilityEnabled,
    viewAvailabilitySettings: callout.viewAvailabilitySettings,
  };
};
